import React, { useEffect, useMemo, useState } from 'react';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	DataTypeEntityOption,
	EntityId,
	EntityType,
	ProjectState,
	resolveEntityName,
	FunctionCallState,
	FunctionDeclarationState,
	READABLE_ENTITY_TYPES,
	lowercaseFirstLetter,
	toSentenceCase,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import {
	Alert,
	Autocomplete,
	TextField,
	Tooltip as MuiTooltip,
	Box,
	Stack,
	Collapse,
} from '@mui/material';
import { EditorService } from '../../../services/editor';
import {
	AllowedDataTypeListTypes,
	ILiteralValueOption,
} from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import { DataTypeBuilderOption } from '../../../services/editor/utils';

export interface IFunctionCallProps {
	project: EditorService;
	entity?: FunctionCallState;
	onClose?: (e: any) => void;
}

export enum FunctionCallError {
	DeclarationRequired = 'declaration-required',
}

export function fromFunctionCallErrorToReadableMessage(
	error: FunctionCallError
): string {
	switch (error) {
		case FunctionCallError.DeclarationRequired:
			return 'Declaration is required';
		default:
			return toSentenceCase(error);
	}
}

function calculateFunctionCallCreationFormErrors(
	entity: FunctionCallState
): FunctionCallError[] {
	const errors: FunctionCallError[] = [];

	if (!entity.declaration) {
		errors.push(FunctionCallError.DeclarationRequired);
	}

	return errors;
}

// Not all entity types can have inputs with any data type
// Sometimes, like in the case of a search, the data type of its inputs can only be a primitive type or a list of primitives
// This function, given an option, will return some metadata that will help it render the option in the data type builder
export function resolveDataTypeOptionConfigForFunctionCall(
	option: DataTypeBuilderOption | ILiteralValueOption,
	input: FunctionCallState
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsTypeConfigForFunctionCall(
	input: FunctionCallState
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeLikeConfigForFunctionCall(
	input: FunctionCallState
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsListConfigForFunctionCall(
	input: FunctionCallState
): {
	allowed: AllowedDataTypeListTypes;
	tooltip: React.ReactNode;
} {
	return {
		allowed: AllowedDataTypeListTypes.All,
		tooltip: null,
	};
}

const FunctionCall: React.FC<IFunctionCallProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: FunctionCallState | null =
		_entity ||
		(project?.logic?.get(entityId) as FunctionCallState | null) ||
		null;

	const [declarationValue, setDeclarationValue] =
		useState<FunctionDeclarationState | null>(entity.declaration || null);

	const [baseFunctionalitySectionOpen, setBaseFunctionalitySectionOpen] =
		useState(true);

	const [errors, setErrors] = useState<FunctionCallError[]>(
		calculateFunctionCallCreationFormErrors(entity)
	);

	useEffect(() => {
		const newErrors = calculateFunctionCallCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.id, {});
	}, [updateKey, declarationValue]);

	const declarationsOptions = useMemo(() => {
		const options = project.logic.functions;

		return options;
	}, [entity.parent]);

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: baseFunctionalitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Custom trigger'
								subtitle='Link this execution to the custom trigger node it executes'
								hideShowSuffix='the base custom trigger section'
								open={baseFunctionalitySectionOpen}
								onToggle={() =>
									setBaseFunctionalitySectionOpen(!baseFunctionalitySectionOpen)
								}
							/>

							<Collapse in={baseFunctionalitySectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<Autocomplete
										id='declaration'
										disableClearable
										disabled={!entity.editable}
										options={declarationsOptions}
										getOptionLabel={(option) =>
											resolveEntityName(option, project.logic)
										}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										value={declarationValue}
										// getOptionDisabled={(option) => {
										// 	const alreadyImplemented = !!entity.parent.inputs.find(
										// 		(p) =>
										// 			p.id !== entity.id && p.implements?.id === option.id
										// 	);

										// 	return alreadyImplemented;
										// }}
										renderOption={(props, option, { selected }) => {
											const { key, ...optionProps } = props;
											return (
												<MuiTooltip
													key={key}
													title={(() => {
														return '';
														// const alreadyImplemented =
														// 	!!entity.parent.inputs.find(
														// 		(p) =>
														// 			p.id !== entity.id &&
														// 			p.implements?.id === option.id
														// 	);

														// if (alreadyImplemented) {
														// 	return 'This base input is already implemented by another input in the current node';
														// }
													})()}
													placement='bottom'
													arrow
													enterDelay={false ? 100 : 400}
													leaveDelay={200}
												>
													<Box
														component='li'
														sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
														{...optionProps}
													>
														<Stack direction='row' gap={1} alignItems='center'>
															<EntityIcon entity={option} project={project} />
															{resolveEntityName(option, project.logic)}
														</Stack>
													</Box>
												</MuiTooltip>
											);
										}}
										onChange={(e, newValue) => {
											const changeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeFuncCallDeclaration
												)
											);

											entity.setDeclaration(newValue);
											changeSet.add(entity, ChangeSetEntityChangeType.Updated);

											entity.afterAllChildrenInitialized(changeSet);

											setDeclarationValue(newValue);

											project.renderAndCloseChangeSet(changeSet);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label='Declaration'
												variant='outlined'
												size='small'
											/>
										)}
									/>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromFunctionCallErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromFunctionCallErrorToReadableMessage(error)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='function-call-builder--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#function-call-builder--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? `Resolve the errors to save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular
								  )}`
								: `Save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular
								  )}`}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default FunctionCall;
